import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { palletAPI, emailAPI } from '../services/api';

const PalletSheetView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sheet, setSheet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [additionalEmail, setAdditionalEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // PDF download state
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  useEffect(() => {
    fetchSheet();
    fetchSummary();
  }, [id]);

  const fetchSheet = async () => {
    try {
      setLoading(true);
      const data = await palletAPI.getSheet(id);
      setSheet(data.sheet);
      // Sort entries by pallet number for better viewing
      const sortedEntries = (data.entries || []).sort((a, b) => {
        const palletA = a.pallet_number || '';
        const palletB = b.pallet_number || '';
        return palletA.localeCompare(palletB, undefined, { numeric: true });
      });
      setEntries(sortedEntries);
      setError('');
    } catch (err) {
      console.error('Failed to fetch sheet:', err);
      setError('Failed to load pallet sheet');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to group entries by pallet number with subtotals
  const getEntriesWithSubtotals = () => {
    if (entries.length === 0) return [];

    const result = [];
    let currentPallet = null;
    let palletSubtotal = 0;
    let grandTotal = 0;

    entries.forEach((entry, index) => {
      const palletNum = entry.pallet_number || '';

      // If new pallet, add subtotal row for previous pallet
      if (currentPallet !== null && palletNum !== currentPallet) {
        result.push({
          type: 'subtotal',
          pallet: currentPallet,
          qty: palletSubtotal
        });
        palletSubtotal = 0;
      }

      // Add the actual entry
      result.push({
        type: 'entry',
        data: entry,
        index: index
      });

      currentPallet = palletNum;
      palletSubtotal += parseInt(entry.qty) || 0;
      grandTotal += parseInt(entry.qty) || 0;

      // If last entry, add final subtotal
      if (index === entries.length - 1) {
        result.push({
          type: 'subtotal',
          pallet: currentPallet,
          qty: palletSubtotal
        });
      }
    });

    // Add grand total row
    result.push({
      type: 'grandtotal',
      qty: grandTotal
    });

    return result;
  };

  const fetchSummary = async () => {
    try {
      const data = await palletAPI.getSummary(id);
      setSummary(data.summary);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloadingPDF(true);
      const response = await palletAPI.downloadPDF(id);

      // Create a blob from the PDF response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `pallet-sheet-${sheet?.customer_name || 'unknown'}-${new Date(sheet?.sheet_date).toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleEmail = async () => {
    try {
      setEmailError('');
      setEmailSuccess('');

      // Fetch email recipients
      const data = await emailAPI.getRecipients();
      setEmailRecipients(data.recipients || []);

      // Select all recipients by default
      setSelectedRecipients(data.recipients?.map(r => r.email) || []);

      // Show modal
      setShowEmailModal(true);
    } catch (err) {
      console.error('Failed to fetch email recipients:', err);
      alert('Failed to load email recipients. Please try again.');
    }
  };

  const handleSendEmail = async () => {
    try {
      setEmailError('');
      setSendingEmail(true);

      // Combine selected recipients and additional email
      let recipients = [...selectedRecipients];
      if (additionalEmail.trim()) {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(additionalEmail.trim())) {
          setEmailError('Please enter a valid email address');
          return;
        }
        recipients.push(additionalEmail.trim());
      }

      if (recipients.length === 0) {
        setEmailError('Please select at least one recipient');
        return;
      }

      await palletAPI.sendEmail(id, recipients);

      setEmailSuccess('Email sent successfully! Status updated to "sent".');

      // Refresh the sheet to show updated status
      await fetchSheet();

      setTimeout(() => {
        setShowEmailModal(false);
        setEmailSuccess('');
        setAdditionalEmail('');
      }, 2000);
    } catch (err) {
      console.error('Failed to send email:', err);
      setEmailError(err.response?.data?.message || 'Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleToggleRecipient = (email) => {
    setSelectedRecipients(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const canEdit = user?.role === 'warehouse_manager' || user?.role === 'admin';

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Loading pallet sheet...</p>
        </div>
      </div>
    );
  }

  if (error || !sheet) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <div style={{ padding: '20px' }}>
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '8px'
          }}>
            {error || 'Sheet not found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        {/* Header - No Print */}
        <div className="no-print" style={{
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '20px'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
              <h1 style={{ margin: 0, fontSize: '24px', color: '#10b981' }}>KFRESH</h1>
              <nav style={{ display: 'flex', gap: '20px' }}>
                <button
                  onClick={() => navigate('/dashboard')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '15px',
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => navigate('/pallets')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '15px',
                    color: '#10b981',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Pallet Sheets
                </button>
                <button
                  onClick={() => navigate('/reports')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '15px',
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Reports
                </button>
                {user?.role === 'admin' && (
                  <button
                    onClick={() => navigate('/admin')}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '15px',
                      color: '#374151',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Admin
                  </button>
                )}
              </nav>
            </div>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>
                  {user?.firstName} {user?.lastName}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
              </div>
              <button
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
          {/* Action Buttons - No Print */}
          <div className="no-print" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <button
              onClick={() => navigate('/pallets')}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#374151',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              ← Back to List
            </button>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleDownloadPDF}
                disabled={downloadingPDF}
                style={{
                  padding: '8px 16px',
                  backgroundColor: downloadingPDF ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: downloadingPDF ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  if (!downloadingPDF) e.target.style.backgroundColor = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  if (!downloadingPDF) e.target.style.backgroundColor = '#3b82f6';
                }}
              >
                {downloadingPDF ? 'Downloading...' : 'Download PDF'}
              </button>
              <button
                onClick={handleEmail}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#7c3aed'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#8b5cf6'}
              >
                Send Email
              </button>
              {canEdit && (
                <button
                  onClick={() => navigate(`/pallets/${id}/edit`)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                >
                  Edit Sheet
                </button>
              )}
            </div>
          </div>

          {/* Sheet Content */}
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {/* Header for Print */}
            <div style={{ marginBottom: '30px', borderBottom: '2px solid #10b981', paddingBottom: '20px' }}>
              <h1 style={{ margin: 0, fontSize: '28px', color: '#10b981' }}>KFRESH PALLET SHEET</h1>
              <p style={{ margin: '8px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                Generated: {new Date().toLocaleString()}
              </p>
            </div>

            {/* Sheet Details */}
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{ fontSize: '18px', marginBottom: '16px', color: '#374151' }}>Sheet Information</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>Sheet Date</div>
                  <div style={{ fontSize: '16px', color: '#111827' }}>{new Date(sheet.sheet_date).toLocaleDateString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>Status</div>
                  <div style={{ fontSize: '16px', color: '#111827' }}>{sheet.status || 'draft'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>Customer Name</div>
                  <div style={{ fontSize: '16px', color: '#111827' }}>{sheet.customer_name || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>Total Entries</div>
                  <div style={{ fontSize: '16px', color: '#111827' }}>{entries.length}</div>
                </div>
              </div>
              {sheet.customer_address && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>Customer Address</div>
                  <div style={{ fontSize: '14px', color: '#111827', whiteSpace: 'pre-wrap' }}>{sheet.customer_address}</div>
                </div>
              )}
            </div>

            {/* Pallet Entries */}
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{ fontSize: '18px', marginBottom: '16px', color: '#374151' }}>Pallet Entries</h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Pallet #</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Grower</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Produce Type</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Unit</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Class</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Size</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Colour</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getEntriesWithSubtotals().map((row, rowIndex) => {
                      if (row.type === 'entry') {
                        const entry = row.data;
                        return (
                          <tr key={entry.id || rowIndex} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '10px 12px', color: '#111827' }}>{entry.pallet_number}</td>
                            <td style={{ padding: '10px 12px', color: '#111827' }}>{entry.grower}</td>
                            <td style={{ padding: '10px 12px', color: '#111827' }}>{entry.produce_type}</td>
                            <td style={{ padding: '10px 12px', color: '#111827' }}>{entry.unit}</td>
                            <td style={{ padding: '10px 12px', color: '#111827' }}>{entry.class_grade}</td>
                            <td style={{ padding: '10px 12px', color: '#111827' }}>{entry.size}</td>
                            <td style={{ padding: '10px 12px', color: '#111827' }}>{entry.colour}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '500', color: '#111827' }}>{entry.qty}</td>
                          </tr>
                        );
                      } else if (row.type === 'subtotal') {
                        return (
                          <tr key={`subtotal-${rowIndex}`} style={{
                            backgroundColor: '#f3f4f6',
                            borderBottom: '2px solid #d1d5db',
                            fontWeight: '600'
                          }}>
                            <td colSpan="7" style={{ padding: '10px 12px', color: '#374151', textAlign: 'right' }}>
                              Pallet #{row.pallet} Subtotal
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#374151' }}>
                              {row.qty.toLocaleString()}
                            </td>
                          </tr>
                        );
                      } else if (row.type === 'grandtotal') {
                        return (
                          <tr key={`grandtotal-${rowIndex}`} style={{
                            backgroundColor: '#d1fae5',
                            borderTop: '3px solid #10b981',
                            fontWeight: '700'
                          }}>
                            <td colSpan="7" style={{ padding: '12px 12px', color: '#065f46', textAlign: 'right', fontSize: '15px' }}>
                              Grand Total
                            </td>
                            <td style={{ padding: '12px 12px', textAlign: 'right', color: '#065f46', fontSize: '15px' }}>
                              {row.qty.toLocaleString()}
                            </td>
                          </tr>
                        );
                      }
                      return null;
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary by Grower/Produce Type */}
            {summary && (
              <div style={{ marginTop: '30px', borderTop: '2px solid #e5e7eb', paddingTop: '20px' }}>
                <h2 style={{ fontSize: '18px', marginBottom: '16px', color: '#374151' }}>Summary by Grower / Produce Type</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                  {summary.byGrowerProduce && summary.byGrowerProduce.map((item, index) => (
                    <div key={index} style={{
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                        <strong>{item.grower}</strong> - {item.produce_type}
                      </div>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>{item.total_qty}</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        {item.entry_count} entries • {item.pallet_count || 0} pallets
                      </div>
                      {item.pallet_numbers && item.pallet_numbers.length > 0 && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                          <strong>Pallets:</strong> {item.pallet_numbers.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#d1fae5',
                    borderRadius: '8px',
                    border: '2px solid #10b981'
                  }}>
                    <div style={{ fontSize: '14px', color: '#065f46', fontWeight: '600', marginBottom: '4px' }}>TOTAL</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#065f46' }}>{summary.totalQty}</div>
                    <div style={{ fontSize: '12px', color: '#047857', marginTop: '4px' }}>
                      {summary.totalEntries} entries • {summary.totalPallets || 0} pallets
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Email Modal */}
        {showEmailModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#374151' }}>
                Send Pallet Sheet via Email
              </h2>

              {/* Success Message */}
              {emailSuccess && (
                <div style={{
                  backgroundColor: '#d1fae5',
                  border: '1px solid #10b981',
                  color: '#065f46',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  {emailSuccess}
                </div>
              )}

              {/* Error Message */}
              {emailError && (
                <div style={{
                  backgroundColor: '#fee2e2',
                  border: '1px solid #ef4444',
                  color: '#991b1b',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  {emailError}
                </div>
              )}

              {/* Recipients List */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Select Recipients
                </label>
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '12px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {emailRecipients.length === 0 ? (
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                      No saved recipients found
                    </p>
                  ) : (
                    emailRecipients.map((recipient, index) => (
                      <label
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRecipients.includes(recipient.email)}
                          onChange={() => handleToggleRecipient(recipient.email)}
                          style={{
                            marginRight: '10px',
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer'
                          }}
                        />
                        <div>
                          <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                            {recipient.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {recipient.email}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Additional Email Input */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Add Additional Email (Optional)
                </label>
                <input
                  type="email"
                  value={additionalEmail}
                  onChange={(e) => setAdditionalEmail(e.target.value)}
                  placeholder="email@example.com"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailError('');
                    setEmailSuccess('');
                    setAdditionalEmail('');
                  }}
                  disabled={sendingEmail}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'white',
                    color: '#374151',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: sendingEmail ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: sendingEmail ? '#9ca3af' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: sendingEmail ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => {
                    if (!sendingEmail) e.target.style.backgroundColor = '#059669';
                  }}
                  onMouseLeave={(e) => {
                    if (!sendingEmail) e.target.style.backgroundColor = '#10b981';
                  }}
                >
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PalletSheetView;
